// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — keeps the globe in the middle of the space the side panels
 * leave free (desktop, single view).
 *
 * The browse panel and the Tools popover open over the inline-end edge
 * of a full-viewport map, so with either open the globe, still centred
 * on the window, reads as off-centre. While one is open the globe glides
 * toward the free side by half the width they take, and glides back when
 * they close.
 *
 * The camera is not touched: MapLibre's own padding would have to be
 * eased through `easeTo`, which cancels the auto-rotate ease and a Khipu's
 * flight, and every frame of it would go out to the sibling panels and
 * the room. The map surface moves instead. `#map-grid` is widened under
 * the panel by the width taken (`extent`), which puts its centre where
 * the globe belongs and keeps the whole window covered, and the move
 * from the old centre to the new one is a CSS transition on `transform`:
 * the grid is first translated back to where the globe was, then let go.
 * The maps are resized once per change, never per frame.
 *
 * The buttons in the inline-end corners (account chip, Help, Enter AR,
 * the Tools bar) step aside by the same measurement, in any layout: this
 * module publishes how far as custom properties and the stylesheets
 * translate them, on the same time and curve.
 */

import { COLUMN_CLASS, SHARES_COLUMN } from './lessonToolsColumn'

/** A panel's horizontal extent, in viewport coordinates. */
export interface PanelBox {
  left: number
  right: number
}

/** Below this the panels are sheets over the globe, not columns beside it. */
const DESKTOP_MIN_WIDTH = 769
/**
 * A panel that takes more than this share of the window is the window
 * (the browse panel in catalog mode): nothing stands beside it. Short
 * of that, both panels open at a large UI scale can take three quarters
 * of a laptop window, and what is left is still where the globe and the
 * buttons belong.
 */
const MAX_INSET_FRACTION = 0.85
/**
 * The least width the two panels together must leave. Under it the info
 * panel, the transport and the corner buttons cannot stand in what is
 * left, and at a large UI scale on a small window the popover would be
 * pushed off the far edge: the panels then take turns instead.
 */
const MIN_FREE_WIDTH = 320
const DURATION_MS = 650
const EASING = 'cubic-bezier(0.45, 0, 0.15, 1)'

/** AYNI: how long a move takes, for what follows the globe by script (`legendPlacement.ts`). */
export const PUSH_DURATION_MS = DURATION_MS
/** AYNI: sent on `window` when the panels' inset changes or the globe starts to move; `detail` is a `PushEventDetail`. */
export const PUSH_EVENT = 'ayni-globe-push'
/** AYNI: sent on `window` when the grid has come to rest after a move. */
export const PUSH_SETTLED_EVENT = 'ayni-globe-settled'
export interface PushEventDetail {
  /** The width the open panels take off the inline-end edge of the window, px (0: nothing open). */
  inset: number
  /** How long the move takes, ms; 0 when the reader asked for less motion, or nothing moves. */
  duration: number
}

/**
 * AYNI: the move's curve, `cubic-bezier(0.45, 0, 0.15, 1)`, as a function
 * of time (0–1 in, 0–1 out), for what has to follow the globe frame by
 * frame rather than by a CSS transition.
 */
export function pushEase(t: number): number {
  if (t <= 0) return 0
  if (t >= 1) return 1
  const [x1, y1, x2, y2] = [0.45, 0, 0.15, 1]
  const bx = (s: number): number => 3 * (1 - s) * (1 - s) * s * x1 + 3 * (1 - s) * s * s * x2 + s * s * s
  const by = (s: number): number => 3 * (1 - s) * (1 - s) * s * y1 + 3 * (1 - s) * s * s * y2 + s * s * s
  // The parameter at which the curve's x is t, by bisection: eight bits are plenty for pixels.
  let lo = 0
  let hi = 1
  for (let i = 0; i < 24; i++) {
    const mid = (lo + hi) / 2
    if (bx(mid) < t) lo = mid
    else hi = mid
  }
  return by((lo + hi) / 2)
}

/**
 * The width the open panels take off the inline-end side of the
 * viewport: from the edge to the farthest panel edge. Even, so the half
 * the globe moves by is a whole pixel. Zero when nothing is open, on a
 * narrow window, or when the panels leave too little to centre in.
 */
export function occupiedInset(
  viewportWidth: number,
  panels: readonly PanelBox[],
  rtl: boolean,
): number {
  if (viewportWidth < DESKTOP_MIN_WIDTH) return 0
  let inset = 0
  for (const box of panels) {
    inset = Math.max(inset, rtl ? box.right : viewportWidth - box.left)
  }
  if (inset <= 0 || inset > viewportWidth * MAX_INSET_FRACTION) return 0
  return Math.round(inset / 2) * 2
}

/** How far an element is translated along x right now, mid-transition included. */
function translateX(el: Element): number {
  const m = /matrix\(([^)]+)\)/.exec(getComputedStyle(el).transform ?? '')
  const x = m ? Number(m[1].split(',')[4]) : 0
  return Number.isFinite(x) ? x : 0
}

/** How far the `translate` property has an element pushed along x right now. */
function pushedX(el: Element): number {
  const x = parseFloat(getComputedStyle(el).translate ?? '')
  return Number.isFinite(x) ? x : 0
}

/**
 * AYNI (owner, 2026-10-22): the stack in the top inline-end corner (the
 * account chip, Help, Enter AR, auto-rotate, Reset and 2D/3D, the licence
 * banner under them), pushed by `--panel-push-corner`. Tools hangs from
 * the bar at the foot of the window: on a tall window it ends well below
 * this stack, which then stays where it is.
 */
const CORNER_STACK = '#account-control, #help-trigger, #vr-enter-btn, #autorotate-control, #globe-view-control, #licence-banner'
/** The least gap between the corner stack and the top of Tools for the stack to stay put, px at UI scale 1. */
const CORNER_CLEARANCE = 8

/** The bottom of the corner stack as drawn, or null when none of it is. */
function cornerBottom(): number | null {
  let bottom: number | null = null
  for (const el of Array.from(document.querySelectorAll(CORNER_STACK))) {
    if (el.getClientRects().length === 0) continue
    bottom = Math.max(bottom ?? -Infinity, el.getBoundingClientRect().bottom)
  }
  return bottom
}

/**
 * How tall the Tools popover is on its tallest tab, capped by its own
 * max-height: what it can grow to without being opened again, so the
 * corner stack does not hop aside and back as the reader changes tabs.
 * The hidden tabs are shown by inline style for the measurement (not by
 * their `hidden` attribute, which lessonToolsColumn.ts observes) and
 * hidden again before anything paints.
 */
function tallestToolsHeight(popover: HTMLElement): number {
  const body = popover.querySelector<HTMLElement>('.tools-menu-popover-body')
  const tabs = Array.from(popover.querySelectorAll<HTMLElement>('.tools-menu-panel'))
  const shown = tabs.find((tab) => !tab.hidden)
  if (!body || !shown) return popover.offsetHeight
  // Everything but the tab on show: the header, the meeting button, the tab row, the rest of the body.
  const chrome = popover.offsetHeight - body.clientHeight + body.scrollHeight - shown.offsetHeight
  const display = getComputedStyle(shown).display
  const others = tabs.filter((tab) => tab.hidden)
  for (const tab of others) tab.style.display = display
  const tallest = Math.max(...tabs.map((tab) => tab.offsetHeight))
  for (const tab of others) tab.style.display = ''
  const cap = parseFloat(getComputedStyle(popover).maxHeight)
  return Number.isFinite(cap) ? Math.min(chrome + tallest, cap) : chrome + tallest
}

export interface GlobePanelOffsetOptions {
  /** The element that holds the map panels (`#map-grid`). */
  grid: HTMLElement
  /**
   * Resize the maps to the grid's new box and repaint them before the
   * browser paints, so the widened grid never shows a stale canvas.
   */
  resizeMaps: () => void
  /**
   * Close the browse panel / the Tools popover. Called when both are
   * open and leave less than `MIN_FREE_WIDTH`: the one opened last
   * stays and the other is closed through these. Without them both
   * stay open.
   */
  closeBrowse?: () => void
  closeTools?: () => void
  /** AYNI: close the Analyze panel, the third side panel (owner, 2026-10-06). */
  closeAnalyze?: () => void
  /** AYNI: close the Studio panel, a side panel too (owner, 2026-10-10). */
  closeStudio?: () => void
  /** AYNI: close the Situation room's panel, a side panel too (Situation room plan, 2026-10-11). */
  closeSituation?: () => void
}

/**
 * AYNI: the side panels that stand beside Browse and Tools, in order:
 * each beside everything before it, pushed by its own property, and
 * telling this module when it opens or closes (`SIDE_PANEL_EVENT`).
 */
const STACKED: ReadonlyArray<{ side: 'analyze' | 'studio' | 'situation' | 'khipu' | 'game'; selector: string; push: string }> = [
  { side: 'analyze', selector: '.analyze-panel:not(.analyze-leaving)', push: '--panel-push-analyze' },
  { side: 'studio', selector: '#studio-panel:not(.studio-leaving)', push: '--panel-push-studio' },
  // AYNI (Situation room, 2026-10-11): the operator's console.
  { side: 'situation', selector: '#situation-panel:not(.situation-leaving)', push: '--panel-push-situation' },
  // The lesson panel, the Khipu player and editor, the room's card (floatingPanel.ts). Never closed to make
  // room (no closer): a lesson or a Khipu someone is presenting stays; the others make way for it.
  { side: 'khipu', selector: '.lesson-player:not(.lesson-leaving):not(.xr-phone-adopted):not(.game-panel)', push: '--panel-push-khipu' },
  // AYNI (meeting games plan): a meeting game's panel (meetingGamesUI.ts), beside the Khipu a trivia game plays. Never closed to make room.
  { side: 'game', selector: '#game-panel:not(.lesson-leaving)', push: '--panel-push-game' },
]

/** AYNI: the event a stacked side panel sends when it opens or closes (analyzeUI.ts `SIDE_PANEL_EVENT`). */
const SIDE_PANEL_EVENT = 'ayni-side-panel'

export interface GlobePanelOffsetHandle {
  /** Measure the panels again and move if what they take has changed. */
  refresh(): void
  dispose(): void
}

/**
 * Start following the browse panel and the Tools popover. Both are found
 * by id on every measurement, so it does not matter which exists yet.
 */
export function initGlobePanelOffset(options: GlobePanelOffsetOptions): GlobePanelOffsetHandle {
  const { grid, resizeMaps } = options
  /** How far the grid currently reaches under the panels, px. */
  let extent = 0
  /** The inset the globe is centred for, or on its way to. */
  let target = 0
  /** AYNI: the whole inset at the last look, to tell a change. */
  let lastInset = 0
  let settleTimer: number | null = null
  /** When each panel was last opened (a counter), and what was open at the last look. */
  type Side = 'browse' | 'tools' | 'analyze' | 'studio' | 'situation' | 'khipu' | 'game'
  const openedAt: Record<Side, number> = { browse: 0, tools: 0, analyze: 0, studio: 0, situation: 0, khipu: 0, game: 0 }
  let clock = 0
  const wasOpen: Record<Side, boolean> = { browse: false, tools: false, analyze: false, studio: false, situation: false, khipu: false, game: false }

  const rtl = (): boolean => document.documentElement.dir === 'rtl'

  /** The grid's translation toward the panels, mid-transition included. */
  const readTranslate = (): number => {
    const x = translateX(grid)
    return rtl() ? -x : x
  }
  const setTranslate = (toward: number): void => {
    grid.style.transform = `translateX(${rtl() ? -toward : toward}px)`
  }
  const setExtent = (next: number): void => {
    if (next === extent) return
    extent = next
    grid.style.insetInlineStart = next ? `${-next}px` : ''
    resizeMaps()
  }

  /** Rest: the grid reaches exactly as far as the panels, untransformed. */
  const settle = (): void => {
    if (settleTimer !== null) {
      clearTimeout(settleTimer)
      settleTimer = null
    }
    grid.style.transition = ''
    grid.style.transform = ''
    setExtent(target)
    // AYNI: the legends put themselves right once more, now the grid stands still (legendPlacement.ts).
    window.dispatchEvent(new CustomEvent(PUSH_SETTLED_EVENT))
  }

  const moveTo = (next: number): void => {
    if (next === target) return
    // Where the globe is now, as a distance from the window's centre.
    const from = extent / 2 - readTranslate()
    target = next
    // Wide enough for both ends of the move, so the window stays covered
    // the whole way; `settle` trims it to the new inset afterwards.
    setExtent(Math.max(extent, next))
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    if (reduceMotion) {
      settle()
      return
    }
    grid.style.transition = 'none'
    setTranslate(extent / 2 - from)
    // Commit the starting position before the transition is switched on.
    void grid.offsetWidth
    grid.style.transition = `transform ${DURATION_MS}ms ${EASING}`
    setTranslate(extent / 2 - next / 2)
    if (settleTimer !== null) clearTimeout(settleTimer)
    settleTimer = window.setTimeout(settle, DURATION_MS + 50)
  }

  /**
   * The corner buttons step aside too, by CSS: the account chip, Help
   * and Enter AR by everything that is open (`--panel-push-top`), the
   * Tools bar and the playback transport under it by the browse panel
   * alone (`--panel-push-bar`), since the popover hangs off that bar. The corner stack takes
   * `--panel-push-corner`, which leaves Tools out when Tools ends below
   * it (`CORNER_STACK`). `buttons-beside-panel` tells the
   * stylesheets the buttons now stand clear of the browse panel and
   * need not be hidden under it.
   */
  const pushButtons = (top: number, bar: number, corner = top): void => {
    const sign = rtl() ? 1 : -1
    const root = document.documentElement.style
    if (top) root.setProperty('--panel-push-top', `${sign * top}px`)
    else root.removeProperty('--panel-push-top')
    if (corner) root.setProperty('--panel-push-corner', `${sign * corner}px`)
    else root.removeProperty('--panel-push-corner')
    if (bar) root.setProperty('--panel-push-bar', `${sign * bar}px`)
    else root.removeProperty('--panel-push-bar')
    // Only on a change: a write that changes nothing still wakes the body's observers (lessonToolsColumn.ts too).
    if (document.body.classList.contains('buttons-beside-panel') !== bar > 0) document.body.classList.toggle('buttons-beside-panel', bar > 0)
  }

  const refresh = (): void => {
    const panels: PanelBox[] = []
    const browse = document.getElementById('browse-overlay')
    if (
      browse &&
      document.body.classList.contains('browse-open') &&
      !browse.classList.contains('hidden')
    ) {
      // The box it is sliding to, not the one mid-slide.
      const width = browse.offsetWidth
      panels.push(rtl() ? { left: 0, right: width } : { left: window.innerWidth - width, right: window.innerWidth })
    }
    const bar = occupiedInset(window.innerWidth, panels, rtl())
    let toolsBox: PanelBox | null = null
    const popover = document.getElementById('tools-menu-popover')
    if (popover && !popover.classList.contains('hidden')) {
      // Where it rests, not where it is while it slides in — and its
      // bar may itself still be on its way to where the browse panel
      // pushes it.
      const rect = popover.getBoundingClientRect()
      const controls = document.getElementById('map-controls')
      const barLag = (rtl() ? bar : -bar) - (controls ? pushedX(controls) : 0)
      const offset = barLag - translateX(popover)
      if (rect.width > 0) {
        toolsBox = { left: rect.left + offset, right: rect.right + offset }
        panels.push(toolsBox)
      }
    }
    const browseOpen = panels.length > 0 && panels[0] !== toolsBox
    const toolsOpen = toolsBox !== null
    // AYNI: Analyze, then the Studio, each stands beside whatever is open
    // before it, pushed by exactly that (its `push` property), so each is
    // measured where it rests.
    const stacked: Partial<Record<Side, number>> = {}
    for (const s of STACKED) {
      const el = document.querySelector<HTMLElement>(s.selector)
      if (!el || window.innerWidth < DESKTOP_MIN_WIDTH) continue
      const width = el.offsetWidth
      if (width <= 0) continue
      // AYNI (owner, 2026-10-11): a lesson panel open with Tools stands in
      // Tools' column, above it and as wide (lessonToolsColumn.ts), pushed
      // as Tools' bar is; Tools' box already counts it.
      if (s.side === 'khipu' && toolsOpen && document.body.classList.contains(COLUMN_CLASS) && el.matches(SHARES_COLUMN)) {
        stacked[s.side] = bar
        continue
      }
      const before = occupiedInset(window.innerWidth, panels, rtl())
      const margin = parseFloat(getComputedStyle(el).insetInlineEnd) || 0
      const near = margin + before
      stacked[s.side] = before
      panels.push(rtl() ? { left: near, right: near + width } : { left: window.innerWidth - near - width, right: window.innerWidth - near })
    }
    const open: Record<Side, boolean> = {
      browse: browseOpen, tools: toolsOpen, analyze: stacked.analyze !== undefined, studio: stacked.studio !== undefined,
      situation: stacked.situation !== undefined,
      khipu: stacked.khipu !== undefined,
      game: stacked.game !== undefined,
    }
    for (const k of Object.keys(open) as Side[]) {
      if (open[k] && !wasOpen[k]) openedAt[k] = ++clock
      wasOpen[k] = open[k]
    }
    const openSides = (Object.keys(open) as Side[]).filter((k) => open[k])
    if (openSides.length > 1 && window.innerWidth >= DESKTOP_MIN_WIDTH) {
      const taken = Math.max(...panels.map((box) => (rtl() ? box.right : window.innerWidth - box.left)))
      if (window.innerWidth - taken < MIN_FREE_WIDTH) {
        // No room for them all: the one opened earliest makes way, the one
        // just opened stays. The change closing makes brings this function
        // round again.
        const closers: Record<Side, (() => void) | undefined> = {
          browse: options.closeBrowse, tools: options.closeTools, analyze: options.closeAnalyze, studio: options.closeStudio,
          situation: options.closeSituation,
          khipu: undefined,
          game: undefined,
        }
        const newest = openSides.reduce((a, b) => (openedAt[a] > openedAt[b] ? a : b))
        const oldest = openSides.filter((k) => k !== newest && closers[k]).sort((a, b) => openedAt[a] - openedAt[b])[0]
        if (oldest) {
          closers[oldest]!()
          return
        }
      }
    }
    const rootStyle = document.documentElement.style
    for (const s of STACKED) {
      const by = stacked[s.side]
      if (by) rootStyle.setProperty(s.push, `${(rtl() ? 1 : -1) * by}px`)
      else rootStyle.removeProperty(s.push)
    }
    const all = occupiedInset(window.innerWidth, panels, rtl())
    // AYNI (owner, 2026-10-22): Tools on its own column, even at its tallest
    // tab ending below the corner stack, leaves the stack where it is. Not
    // while a lesson shares its column: the lesson stands at the top.
    let corner = all || bar
    if (popover && toolsBox && !document.body.classList.contains(COLUMN_CLASS)) {
      const stackBottom = cornerBottom()
      const scale = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--ui-scale')) || 1
      const toolsTop = popover.getBoundingClientRect().bottom - tallestToolsHeight(popover)
      if (stackBottom !== null && toolsTop >= stackBottom + CORNER_CLEARANCE * scale) {
        corner = occupiedInset(window.innerWidth, panels.filter((box) => box !== toolsBox), rtl()) || bar
      }
    }
    pushButtons(all || bar, bar, corner)
    const root = document.documentElement.style
    const sign = rtl() ? 1 : -1
    // For what is centred on the window (the date label): the middle of
    // the space left, which is half as far, in any layout.
    if (all) root.setProperty('--panel-push-globe', `${(sign * all) / 2}px`)
    else root.removeProperty('--panel-push-globe')
    // Two or four globes share the window; there is no one globe to centre.
    const globe = grid.children.length > 1 ? 0 : all
    // For what is anchored to the inline-end edge of the globe's own
    // panel (the map's corner controls).
    if (globe) root.setProperty('--panel-push-view', `${sign * globe}px`)
    else root.removeProperty('--panel-push-view')
    // AYNI (owner, 2026-10-11): the whole inset, in any layout, for what
    // keeps itself in the space the panels leave (the legends,
    // legendPlacement.ts): they are not pushed, they stay in view.
    if (all) root.setProperty('--panel-inset', `${all}px`)
    else root.removeProperty('--panel-inset')
    const changed = all !== lastInset || globe !== target
    lastInset = all
    moveTo(globe)
    if (changed) {
      const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
      window.dispatchEvent(new CustomEvent<PushEventDetail>(PUSH_EVENT, { detail: { inset: all, duration: reduceMotion ? 0 : DURATION_MS } }))
    }
  }

  // Both panels are opened and closed by class: `browse-open` on the
  // body, `hidden` on the popover (which the Tools bar may rebuild).
  const observer = new MutationObserver(refresh)
  const classes = { attributes: true, attributeFilter: ['class'] }
  observer.observe(document.body, classes)
  const controls = document.getElementById('map-controls')
  if (controls) observer.observe(controls, { ...classes, subtree: true, childList: true })
  // Panels come and go with the layout.
  observer.observe(grid, { childList: true })
  // The panels change width without changing class: the UI-scale
  // presets, a section arriving late in the popover.
  const sizes = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(refresh)
  for (const id of ['browse-overlay', 'tools-menu-popover']) {
    const el = document.getElementById(id)
    if (el) sizes?.observe(el)
  }
  window.addEventListener('resize', refresh)
  window.addEventListener(SIDE_PANEL_EVENT, refresh)
  refresh()

  return {
    refresh,
    dispose(): void {
      observer.disconnect()
      sizes?.disconnect()
      window.removeEventListener('resize', refresh)
      window.removeEventListener(SIDE_PANEL_EVENT, refresh)
      pushButtons(0, 0)
      for (const s of STACKED) document.documentElement.style.removeProperty(s.push)
      document.documentElement.style.removeProperty('--panel-push-globe')
      document.documentElement.style.removeProperty('--panel-push-view')
      document.documentElement.style.removeProperty('--panel-inset')
      target = 0
      lastInset = 0
      settle()
    },
  }
}
